import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AdminApi } from "../api/admin-api";
import AdminLayout from "../components/AdminLayout";
import { toast } from "sonner";

interface User {
  id: number;
  externalId: string;
  firstName: string | null;
  lastName: string | null;
  contactEmail: string;
  createdAt: string;
}

interface BillingAccount {
  billingAccountId: number;
  billingAccountExternalId: string;
  creditBalance: number;
  teamId: number;
  teamExternalId: string;
  teamName: string | null;
  stripeCustomerId: string | null;
}

export default function AddCredits() {
  const [email, setEmail] = useState("");
  const [user, setUser] = useState<User | null>(null);
  const [billingAccounts, setBillingAccounts] = useState<BillingAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null,
  );
  const [creditAmount, setCreditAmount] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const addCreditsMutation = useMutation({
    mutationFn: async ({
      billingAccountId,
      credits,
    }: {
      billingAccountId: number;
      credits: number;
    }) => {
      return AdminApi.addCredits(billingAccountId, credits, email);
    },
    onSuccess: (data) => {
      setSuccess(
        `Successfully added ${creditAmount} credits! New balance: ${data.newBalance}`,
      );
      toast.success("Credits added successfully");

      // Update the local billing account balance
      setBillingAccounts((prev) =>
        prev.map((account) =>
          account.billingAccountId === selectedAccountId
            ? { ...account, creditBalance: data.newBalance }
            : account,
        ),
      );

      // Reset form
      setCreditAmount("");
      setSelectedAccountId(null);
    },
    onError: (error) => {
      console.error("Failed to add credits:", error);
      setError(`Failed to add credits: ${error.message}`);
      toast.error("Failed to add credits");
    },
  });

  const handleSearchUser = async () => {
    if (!email.trim()) {
      setError("Please enter an email address");
      return;
    }

    setIsSearching(true);
    setError(null);
    setSuccess(null);
    setUser(null);
    setBillingAccounts([]);
    setSelectedAccountId(null);
    setCreditAmount("");

    try {
      const userData = await AdminApi.getUserByEmail(email.trim());
      setUser(userData);

      // Load billing accounts for the user
      const accounts = await AdminApi.getUserBillingAccounts(userData.id);
      setBillingAccounts(accounts);

      if (accounts.length === 0) {
        setError(
          "This user has no billing accounts. Users need to be part of a team to have billing accounts.",
        );
      }
    } catch (err: any) {
      console.error("Search failed:", err);
      if (err.message?.includes("not found") || err.response?.status === 404) {
        setError(`No user found with email: ${email}`);
      } else {
        setError(`Failed to search for user: ${err.message}`);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedAccountId || !creditAmount) {
      setError("Please select a billing account and enter a credit amount");
      return;
    }

    const credits = parseInt(creditAmount);
    if (isNaN(credits) || credits < 1) {
      setError("Credit amount must be a positive number");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to add ${credits} credits to this account? This action cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    setError(null);
    setSuccess(null);
    addCreditsMutation.mutate({ billingAccountId: selectedAccountId, credits });
  };

  const selectedAccount = billingAccounts.find(
    (account) => account.billingAccountId === selectedAccountId,
  );

  return (
    <AdminLayout
      title="Add Credits to User"
      description="Search for a user by email and add credits to their billing account"
      breadcrumb="Add Credits"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Step 1: User Search */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-3">1. Search User</h2>
          <div className="flex gap-3">
            <input
              type="email"
              placeholder="Enter user email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSearching}
            />
            <button
              onClick={handleSearchUser}
              disabled={isSearching || !email.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* User Information */}
        {user && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">User Found</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Name:</span>{" "}
                {user.firstName || user.lastName
                  ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                  : "Not provided"}
              </div>
              <div>
                <span className="font-medium">Email:</span> {user.contactEmail}
              </div>
              <div>
                <span className="font-medium">User ID:</span> {user.id}
              </div>
              <div>
                <span className="font-medium">Created:</span>{" "}
                {new Date(user.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Billing Account Selection */}
        {billingAccounts.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">
              2. Select Billing Account
            </h2>
            <div className="space-y-3">
              {billingAccounts.map((account) => (
                <label
                  key={account.billingAccountId}
                  className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-100"
                >
                  <input
                    type="radio"
                    name="billingAccount"
                    value={account.billingAccountId}
                    checked={selectedAccountId === account.billingAccountId}
                    onChange={(e) =>
                      setSelectedAccountId(parseInt(e.target.value))
                    }
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-lg">
                      {account.teamName || `Team ${account.teamId}`}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        <span className="font-medium">Current Balance:</span>{" "}
                        <span className="text-lg font-semibold text-green-600">
                          {account.creditBalance} credits
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Account ID:</span>{" "}
                        {account.billingAccountId}
                      </div>
                      <div>
                        <span className="font-medium">Team ID:</span>{" "}
                        {account.teamExternalId}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Add Credits */}
        {selectedAccount && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">3. Add Credits</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <div className="font-medium">Selected Account:</div>
                <div className="text-sm text-gray-600">
                  {selectedAccount.teamName || `Team ${selectedAccount.teamId}`}{" "}
                  - Current balance: {selectedAccount.creditBalance} credits
                </div>
              </div>

              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">
                    Credits to Add
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter number of credits"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={addCreditsMutation.isPending}
                  />
                </div>
                <button
                  onClick={handleAddCredits}
                  disabled={
                    addCreditsMutation.isPending ||
                    !creditAmount ||
                    !selectedAccountId
                  }
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addCreditsMutation.isPending ? "Adding..." : "Add Credits"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="text-red-800 font-medium">Error</div>
            <div className="text-red-600 text-sm mt-1">{error}</div>
          </div>
        )}

        {/* Success Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="text-green-800 font-medium">Success</div>
            <div className="text-green-600 text-sm mt-1">{success}</div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
